import {
  AllieText,
  AllieChip,
  AllieItem,
  AllieLabel,
  AlliePriorityFlagOptions,
  AllieSelectOptionProps,
} from '../../../../../components';
import { AllieColumnLink } from '../../../ColumnLink';
import { AllieControlledSelect } from '../../../ControlledSelect';
import { AllieDateField } from '../../../DateField';
import { AlliePriority } from '../../../Priority';
import { DataContent } from '../Contents/DataContent';
import { DueDate } from '../DueDate/DueDate';
import './Details.css';

export interface DetailsProps {
  id: number;
  publicId: string;
  machine: {
    name: string;
  };
  machineGroup: {
    groupName: string;
  };
  initiatedBy: {
    avatar: string;
    name: string;
  };
  assignedTo: {
    id: number;
    avatar: string;
    name: string;
  };
  incident: {
    id: number;
    title: string;
    dateCreated?: number;
  };
  type: string;
  dueDate: number;
  dateCreated: number;
  priority: string;
  status: string;
  statusAvailables: AllieSelectOptionProps[];
  statusChips: Record<string, any>;
  statusShowSelect: boolean;
  onChangeStatus: (e: any) => any;
  operatorsOptions: { text: string, value: number }[];
  onChangeAssigned: (e: any) => any;
  overdue: boolean;
  labels: {
    detail: string;
    machine: string;
    machineGroup: string;
    status: string;
    creationDate: string;
    assignedTo: string;
    dueDate: string;
    reporter: string;
    incident: string;
    priority: string;
    statusEnum: Record<string, string>;
    typesEnum: Record<string, string>;
    type: string;
    overdue: string;
  };
}

const namespace = 'allie-detail-card-detail';

export const Details: React.FC<DetailsProps> = ({
  id,
  publicId,
  machine,
  machineGroup,
  initiatedBy,
  assignedTo,
  type,
  dueDate,
  dateCreated,
  priority,
  incident,
  status,
  statusAvailables,
  statusChips,
  statusShowSelect,
  onChangeStatus,
  operatorsOptions,
  onChangeAssigned,
  overdue,
  labels,
}) => {
  return (
    <div className={namespace}>
      <div className={`${namespace}__header`}>
        <AllieText type="h3">{labels.detail}</AllieText>
        {statusAvailables && status && (
          <div className={`${namespace}__status`}>
            {statusShowSelect ? (
              <AllieControlledSelect
                options={statusAvailables}
                initialValue={status}
                onSelect={onChangeStatus}
              />
            ) : (
              <AllieChip
                className={`${namespace}__status-chip`}
                allieType={statusChips[status]}
                style={{ borderRadius: '4px', cursor: 'default' }}
              >
                {labels.statusEnum[status as any]}
              </AllieChip>
            )}
          </div>
        )}
      </div>
      <div className={`${namespace}__content`}>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <DataContent type="text" title="ID" data={{ text: publicId }} />
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <DataContent type="text" title={labels.machineGroup} data={{ text: machineGroup || '-' }} />
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <DataContent type="text" title={labels.machine} data={{ text: machine.name }} />
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <DataContent
              type="userAssigned"
              title={labels.reporter}
              data={{ avatar: initiatedBy?.avatar, name: initiatedBy?.name }}
            />
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <DataContent
              type="userAssigned"
              title={labels.assignedTo}
              data={{
                avatar: assignedTo?.avatar,
                name: assignedTo?.name,
                selected: assignedTo?.id,
                operators: operatorsOptions,
                onSelect: onChangeAssigned,
              }}
            />
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <AllieText type="h4" color="neutral-70">
              {labels.incident}
            </AllieText>
            {incident ? (
              <AllieColumnLink
                href={`/incidents/${incident.id}`}
                triggerId={`link-work-orders-${id}-incident-${incident.id}`}
                value={incident.title}
                className={`${namespace}__column-link`}
              />
            ) : (
              '-'
            )}
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <AllieText type="h4" color="neutral-70">
              {labels.type}
            </AllieText>
            <AllieText type="body1" color="neutral">
              {labels.typesEnum[type as any]}
            </AllieText>
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <div className={`${namespace}__date`}>
              <div>
                <AllieText type="h4" color="neutral-70">
                  {labels.creationDate}
                </AllieText>
                <AllieText type="body1">
                  <AllieDateField timestamp={dateCreated} format="DD-MM-YYYY HH:mm" />
                </AllieText>
              </div>
            </div>
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="full">
          <AllieLabel>
            <div className={`${namespace}__date`}>
              <div>
                <AllieText type="h4" color="neutral-70">
                  {labels.dueDate}
                </AllieText>
                <DueDate value={dueDate} overdue={overdue} labelOverDue={labels.overdue} />
              </div>
            </div>
          </AllieLabel>
        </AllieItem>
        <AllieItem className={`${namespace}__item`} lines="none">
          <AllieLabel>
            <AllieText type="h4" color="neutral-70">
              {labels.priority}
            </AllieText>
            <AlliePriority value={Number(priority) as AlliePriorityFlagOptions} />
          </AllieLabel>
        </AllieItem>
      </div>
    </div>
  );
};
